// ABOUTME: Single source of truth for the page title shown in the top-bar
// breadcrumb. Both the Header (which renders it) and PageHeader (which hides
// its own h1 when it would merely repeat it) read from here, so the two can
// never drift into showing the same word twice.

import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useBreadcrumbStore } from '@/stores/useBreadcrumbStore'

export const routeLabels: Record<string, string> = {
  dashboard: 'Dashboard',
  reviews: 'Reviews',
  results: 'Results',
  files: 'Files',
  library: 'Library',
  chat: 'Marketing',
  crm: 'CRM',
  content: 'Content',
  campaigns: 'Campaigns',
  new: 'New',
  attribution: 'Attribution',
  analytics: 'Analytics',
  billing: 'Billing',
  settings: 'Settings',
  notifications: 'Notifications',
  drafts: 'Drafts',
  calendar: 'Calendar',
  media: 'Media Library',
  leads: 'Leads',
  contacts: 'Customers',
  revenue: 'Revenue',
  gmb: 'GMB',
  scheduled: 'Scheduled Reports',
  links: 'Link Creator',
  business: 'Business',
  accounts: 'Accounts',
  team: 'Team',
  plan: 'Plan & credits',
  you: 'You',
  advanced: 'Advanced',
  'voice-clone': 'Your own voice',
  prompts: 'Prompt Library',
  admin: 'Admin Dashboard',
  approvals: 'Approvals',
  ads: 'Ads',
  'agent-tasks': 'Agent tasks',
  bookings: 'Bookings',
  businesses: 'Businesses',
  conversations: 'Conversations',
  image: 'Image editor',
  inbox: 'Inbox',
  insights: 'Insights',
  learning: 'Learning',
  mfa: 'Two-factor authentication',
  performance: 'Performance',
  posts: 'Posts',
  shopify: 'Shopify',
  sms: 'SMS',
  triage: 'Triage',
  'video-editor': 'Video editor',
  connections: 'Connections',
}

// A path segment with no label of its own: an id reads as "Details", anything
// else as sentence case ("some-page" -> "Some page").
function humanizeSegment(segment: string): string {
  if (/^\d+$|^[0-9a-f]{8,}(-[0-9a-f]{4,})*$/i.test(segment)) return 'Details'
  const words = segment.replace(/[-_]+/g, ' ').trim()
  return words.charAt(0).toUpperCase() + words.slice(1)
}

export interface Crumb {
  path: string
  label: string
  isLast: boolean
}

export function generateBreadcrumbs(pathname: string, overrides: Record<string, string>): Crumb[] {
  const segments = pathname.split('/').filter(Boolean)
  return segments.map((segment, index) => {
    const path = '/' + segments.slice(0, index + 1).join('/')
    const label = overrides[path] || routeLabels[segment] || humanizeSegment(segment)
    const isLast = index === segments.length - 1
    return { path, label, isLast }
  })
}

/**
 * The label the top bar is currently showing as the page title — the last
 * crumb. `null` at the root, where there are no segments to name.
 *
 * Only safe inside a Router; callers that may render outside one (page unit
 * tests render pages bare) must gate on `useInRouterContext()` first.
 */
export function useCurrentPageLabel(): string | null {
  const { pathname } = useLocation()
  const overrides = useBreadcrumbStore((state) => state.labels)
  const crumbs = generateBreadcrumbs(pathname, overrides)
  return crumbs.length > 0 ? crumbs[crumbs.length - 1].label : null
}

/**
 * Would rendering `title` as a page heading just repeat the top bar?
 *
 * Compared case- and whitespace-insensitively, and only for plain strings —
 * a ReactNode title (an editable field, a dynamic record name) is never
 * treated as a duplicate, because we cannot read what it will render.
 */
export function duplicatesBreadcrumb(title: unknown, crumbLabel: string | null): boolean {
  if (typeof title !== 'string' || !crumbLabel) return false
  const normalize = (value: string) => value.trim().toLowerCase()
  return normalize(title) === normalize(crumbLabel)
}

/** The browser tab / app switcher title: the page name, then the product. */
export function useDocumentTitle(): void {
  const label = useCurrentPageLabel()
  useEffect(() => {
    document.title = label ? `${label} · Maxflow` : 'Maxflow'
  }, [label])
}
